import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { useSession } from 'next-auth/react';
import { useForm, FormProvider } from 'react-hook-form';
import { isEmpty } from 'lodash';
import qs from 'query-string';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import * as gtag from '@/lib/gtag';
import WelcomeToFloodPortal from '@/components/welcome-to-flood-portal';
import { ACCESS_POLICY, FILE_A_CLAIM, PAY_RENEWAL } from '@/lib/constants';

export const schema = yup.object().shape({
    policyNumber: yup
        .string()
        .required('This is a required field.')
        .max(14)
        .matches(/^\d{14}$/, 'Please enter a valid policy number.'),
    name: yup.string().required('This is a required field.'),
    zipCode: yup
        .string()
        .required('This is a required field.')
        .max(5)
        .matches(/^\d{5}$/, 'Please enter a valid zip code.'),
    likeToDo: yup.string().required('This is a required field.')
});

export const GET_ACTIVE_RENEWAL = gql`
    query GetActiveRenewal(
        $policyTermNumber: String!
        $name: String!
        $zip: String!
    ) {
        renewal: getActiveRenewalByPolicyTermNumber(
            policyTermNumber: $policyTermNumber
            name: $name
            zip: $zip
        ) {
            success {
                pre {
                    identifiers {
                        policyTermNumber
                    }
                    possiblePremiums {
                        buildingCoverage {
                            units
                        }
                        buildingDeductible {
                            units
                        }
                        totalAmount {
                            units
                        }
                        coverageType
                    }
                }
            }
            insOrgProfile {
                name
                contact {
                    contactPhone
                }
            }
            tenant {
                tenantCode
            }
        }
    }
`;

export const GET_MOST_RECENT_TERM = gql`
    query GetMostRecentTerm(
        $policyTermNumber: String!
        $name: String!
        $zip: String!
    ) {
        term: getMostRecentTermByPolicyTermNumber(
            policyTermNumber: $policyTermNumber
            name: $name
            zip: $zip
        ) {
            success {
                pre {
                    identifiers {
                        policyTermNumber
                    }
                }
            }
        }
    }
`;

export const ADD_POLICY = gql`
    mutation AddPolicy($policyNumber: String!, $name: String!, $zip: String!) {
        policy: insuredPortalAddPolicy(
            policyNumber: $policyNumber
            name: $name
            zip: $zip
        ) {
            id
        }
    }
`;

/**
 *
 * @param {*} props
 * @returns
 */
const Home = props => {
    const { authenticated, selected, title } = props;

    const router = useRouter();
    const [noAccess, setNoAccess] = useState(false);
    const [noPolicy, setNoPolicy] = useState(false);
    const [noRenewal, setNoRenewal] = useState(false);
    const [notEligibleRenewal, setNotEligibleRenewal] = useState(false);
    const [notEligibleRollover, setNotEligibleRollover] = useState(false);

    const { data: session } = useSession();

    const methods = useForm({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            policyNumber: '',
            name: '',
            zipCode: '',
            likeToDo: selected
        }
    });

    const { getValues, handleSubmit } = methods;

    const [
        getActiveRenewal,
        { data: getActiveRenewalData, loading: getActiveRenewalLoading }
    ] = useLazyQuery(GET_ACTIVE_RENEWAL, {
        onCompleted: data => {
            const { errors } = data?.renewal?.errors ?? {};

            const { policyTermNumber } =
                data?.renewal?.success?.pre?.identifiers ?? {};

            if (isEmpty(errors)) {
                const { name, zipCode: zip } = getValues();

                router.push(
                    `/pay-renewal-bill/${policyTermNumber}?${qs.stringify({
                        name,
                        zip
                    })}`
                );
            }
        },
        onError: ({ message }) => {
            if (message?.match(/^Unable to find a renewal/)) {
                setNoRenewal(true);
            } else if (message?.match(/^Ineligible to rollover/)) {
                setNotEligibleRollover(true);
            } else if (message?.match(/^Ineligible to renew/)) {
                setNotEligibleRenewal(true);
            } else {
                setNoPolicy(true);
            }
        }
    });

    const [addPolicy, { loading: addPolicyLoading }] = useMutation(ADD_POLICY);

    const { policyTermNumber } =
        getActiveRenewalData?.success?.pre?.identifiers ?? {};

    const [
        getMostRecentTerm,
        { loading: getMostRecentTermLoading }
    ] = useLazyQuery(GET_MOST_RECENT_TERM, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: async data => {
            const { errors } = data?.term?.errors ?? {};

            const { policyTermNumber } =
                data?.term?.success?.pre?.identifiers ?? {};

            if (!isEmpty(errors) || !Boolean(policyTermNumber)) {
                setNoPolicy(true);
            } else {
                const { name, zipCode: zip, likeToDo } = getValues();

                let path = `/file-a-claim/${policyTermNumber}?${qs.stringify({
                    name,
                    zip
                })}`;

                if (likeToDo === ACCESS_POLICY) {
                    try {
                        await addPolicy({
                            variables: {
                                policyNumber: policyTermNumber,
                                name,
                                zip
                            }
                        });

                        path = `/policy/${policyTermNumber}?${qs.stringify({
                            name,
                            zip
                        })}`;
                    } catch (error) {
                        setNoAccess(true);
                    }
                }

                router.push(path);
            }
        },
        onError: () => {
            setNoPolicy(true);
        }
    });

    const onSubmit = data => {
        const {
            likeToDo,
            name,
            policyNumber: policyTermNumber,
            zipCode: zip
        } = data;

        setNoAccess(false);
        setNoRenewal(false);
        setNoPolicy(false);
        setNotEligibleRollover(false);
        setNotEligibleRenewal(false);

        gtag.event('search', {
            search_term: JSON.stringify({
                name,
                policyTermNumber,
                zip
            })
        });

        if (likeToDo === PAY_RENEWAL) {
            getActiveRenewal({
                variables: {
                    name,
                    policyTermNumber,
                    zip
                }
            });
        } else {
            getMostRecentTerm({
                variables: {
                    name,
                    policyTermNumber,
                    zip
                }
            });
        }
    };

    const handleReset = () => {
        setNoAccess(false);
        setNoRenewal(false);
        setNoPolicy(false);
        setNotEligibleRollover(false);
        setNotEligibleRenewal(false);
    };

    // Send Google Analytics event
    useEffect(() => {
        const { name, zipCode: zip } = getValues();

        gtag.event('view_search_results', {
            search_term: JSON.stringify({
                name,
                policyTermNumber,
                zip
            })
        });
    }, [
        policyTermNumber,
        noPolicy,
        noRenewal,
        notEligibleRenewal,
        notEligibleRollover,
        getValues
    ]);

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <WelcomeToFloodPortal
                    authenticated={authenticated || Boolean(session)}
                    selected={selected}
                    onReset={handleReset}
                    noAccess={noAccess}
                    noRenewal={noRenewal}
                    noPolicy={noPolicy}
                    notEligibleRenewal={notEligibleRenewal}
                    notEligibleRollover={notEligibleRollover}
                    submitting={
                        getActiveRenewalLoading ||
                        getMostRecentTermLoading ||
                        addPolicyLoading
                    }
                    title={title}
                />
            </form>
        </FormProvider>
    );
};

Home.propTypes = {
    authenticated: PropTypes.bool,
    selected: PropTypes.string,
    title: PropTypes.string
};

Home.defaultProps = {
    authenticated: false,
    selected: PAY_RENEWAL,
    title: null
};

export default Home;
